import React, { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FlagRounded from '@mui/icons-material/FlagRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import getDuration from 'utils/getDuration';
import MLDialog from 'components/common/Dialog/Dialog';
import TSButton from 'components/common/Button/Button';
import FactList from 'components/common/FactList/FactList';
import Notice from 'components/common/Notice/Notice';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import TSCard from 'components/common/TSCard/TSCard';
import CheckIns from '../CheckIns/CheckIns';

const ActivityDetail = ({ activity, colourScheme = 'display' }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const [showCheckins, setShowCheckins] = useState(false);

  const now = dayjs();
  const startDate = dayjs.utc(activity.ActivityStartDate);
  const endDate = dayjs.utc(activity.ActivityEndDate ?? now);

  const duration = getDuration({
    start: startDate,
    end: endDate,
    expected: activity.ExpectedEndDate,
  });
  const badgeStatus = duration.isOverdue
    ? duration.isLate
      ? 'warning'
      : 'error'
    : 'good';

  const badge = activity.ActivityEndDate
    ? 'finished'
    : { good: 'onTrack', warning: 'late', error: 'overdue' }[badgeStatus];

  return (
    <div className="activity-detail w-full">
      <TSCard
        tone={badge === 'overdue' ? undefined : 'brand'}
        title={`${activity.TrailName}, ${activity.ParkName}`}
        action={<StatusBadge status={badge} />}
      >
        <FactList>
          <li>
            <FlagRounded aria-hidden="true" />
            <span>
              Started{' '}
              {dayjs
                .utc(activity.ActivityStartDate)
                .local()
                .format('ddd D MMM, h:mm A')}
            </span>
          </li>
          <li>
            <ScheduleRounded aria-hidden="true" />
            <span>
              {activity.ActivityEndDate ? 'Finished' : 'Expected back'}{' '}
              <strong>
                {dayjs
                  .utc(activity.ActivityEndDate ?? activity.ExpectedEndDate)
                  .local()
                  .format('ddd D MMM, h:mm A')}
              </strong>
            </span>
          </li>
          <li>
            <TimerRounded aria-hidden="true" />
            <span>Out for {duration.durationText}</span>
          </li>
          <li>
            <PeopleAltRounded aria-hidden="true" />
            <span>
              {activity.AdditionalParticipants + 1 /* include user */}{' '}
              {activity.AdditionalParticipants === 0 ? 'walker' : 'walkers'}
            </span>
          </li>
        </FactList>

        {duration.isOverdue && !activity.ActivityEndDate && (
          <Notice tone={duration.isLate ? 'warning' : 'danger'}>
            {duration.isLate
              ? `This walk is running late by ${duration.overdueText}.`
              : `This walk is overdue by ${duration.overdueText}.`}
          </Notice>
        )}

        {activity?.CheckIns?.length > 0 && (
          <div>
            <TSButton
              btnText={`See ${activity.CheckIns.length} check-ins`}
              btnVariant="text"
              onClickFunc={() => setShowCheckins(true)}
            />
          </div>
        )}
      </TSCard>

      <MLDialog
        openDialog={showCheckins}
        setOpenDialog={setShowCheckins}
        classList={'activity-dashboard-dialog'}
      >
        <CheckIns
          checkins={[...activity.CheckIns]}
          colourScheme="Dashboard"
          dialog={true}
          handleClose={() => setShowCheckins(false)}
        />
      </MLDialog>
    </div>
  );
};

export default ActivityDetail;
